import type { OfferView, RecommendationView } from "../api/client.js";
import type {
  PrototypeGame,
  PrototypeHealthView,
  PrototypeLedgerRow,
  PrototypeLineupCandidate,
} from "./types.js";

const AU_BOOKMAKERS = [
  "sportsbet",
  "bet365",
  "tab",
  "pointsbet",
  "ladbrokes",
  "dabble",
  "picklebet",
  "betr",
  "nextbet",
] as const;

function offers(
  line: number | null,
  prices: readonly number[],
  draftKingsPrice: number,
): readonly OfferView[] {
  return [
    ...AU_BOOKMAKERS.map((bookmaker, index) => ({
      bookmaker,
      line,
      price: prices[index] ?? prices[0] ?? 1.8,
      role: "AU_EXECUTION" as const,
      // Dabble is the one book that publishes no event URL, so the prototype
      // carries that gap too - a board where every row links is not the board
      // the operator actually gets.
      url:
        bookmaker === "dabble"
          ? null
          : `https://${bookmaker}.example/event/prototype`,
    })),
    {
      bookmaker: "draftkings",
      line,
      price: draftKingsPrice,
      role: "US_REFERENCE" as const,
      url: null,
    },
  ];
}

/**
 * Fixture tip times are relative to now rather than fixed, so the home board
 * countdown and its soonest-first ordering are exercised whenever the
 * prototype is opened. A fixed date would show every game as long gone.
 */
function inMinutes(minutes: number): string {
  return new Date(Date.now() + minutes * 60_000).toISOString();
}

export const PROTOTYPE_GAMES: readonly PrototypeGame[] = [
  {
    gameId: "prototype-game-bos-nyk",
    gameLabel: "BOS @ NYK",
    melbourneTip: "11:30 AM AEDT",
    tipUtc: inMinutes(12),
  },
  {
    gameId: "prototype-game-mia-chi",
    gameLabel: "MIA @ CHI",
    melbourneTip: "12:00 PM AEDT",
    tipUtc: inMinutes(46),
  },
  {
    gameId: "prototype-game-den-phx",
    gameLabel: "DEN @ PHX",
    melbourneTip: "1:30 PM AEDT",
    tipUtc: inMinutes(145),
  },
  {
    gameId: "prototype-game-sac-lal",
    gameLabel: "SAC @ LAL",
    melbourneTip: "2:00 PM AEDT",
    tipUtc: inMinutes(232),
  },
];

export const PROTOTYPE_RECOMMENDATIONS: readonly RecommendationView[] = [
  {
    signalId: "prototype-signal-3pm",
    melbourneTip: "11:30 AM AEDT",
    tipUtc: inMinutes(12),
    ruleVersion: "THREE_PM_REVERSION_v1.0",
    description: "Marcus Vale Under 2.5 3PM",
    gameLabel: "BOS @ NYK",
    bookmaker: "sportsbet",
    line: 2.5,
    price: 1.95,
    minimumOdds: 1.84,
    units: 0.5,
    stakeCents: 1_000,
    status: "RECOMMENDED",
    evidence: [
      "Previous game: 34 minutes",
      "Previous game: 1 made three",
      "Exact-line conservative probability: 54.9%",
      "Best AU price clears minimum by 0.11",
    ],
    offers: offers(
      2.5,
      [1.95, 1.91, 1.9, 1.88, 1.93, 1.87, 1.89, 1.9, 1.86],
      1.84,
    ),
  },
  {
    signalId: "prototype-signal-road",
    melbourneTip: "12:00 PM AEDT",
    tipUtc: inMinutes(46),
    ruleVersion: "ROAD_B2B_BOUNCE_v1.0",
    description: "Eli Mercer Over 21.5 points",
    gameLabel: "MIA @ CHI",
    bookmaker: "ladbrokes",
    line: 21.5,
    price: 2.02,
    minimumOdds: 1.91,
    units: 0.5,
    stakeCents: 1_000,
    status: "RECOMMENDED",
    evidence: [
      "Road back-to-back qualifier",
      "Prior game scoring below adjusted baseline",
      "Exact-line conservative probability: 52.7%",
      "AU consensus excludes DraftKings",
    ],
    offers: offers(
      21.5,
      [1.94, 1.96, 1.93, 1.98, 2.02, 1.9, 1.91, 1.97, 1.92],
      2.06,
    ),
  },
  {
    signalId: "prototype-signal-short-rest",
    melbourneTip: "1:30 PM AEDT",
    tipUtc: inMinutes(145),
    ruleVersion: "SHORT_REST_DOG_v1.0",
    description: "PHX +5.5 spread",
    gameLabel: "DEN @ PHX",
    bookmaker: "tab",
    line: 5.5,
    price: 1.94,
    minimumOdds: 1.88,
    units: 0.5,
    stakeCents: 1_000,
    status: "RECOMMENDED",
    evidence: [
      "Home underdog on qualifying short rest",
      "AU consensus line: +5.5",
      "Exact-line conservative cover probability: 54.0%",
      "Portfolio game exposure remains below 6u",
    ],
    offers: offers(
      5.5,
      [1.9, 1.91, 1.94, 1.89, 1.92, 1.9, 1.88, 1.91, 1.87],
      1.96,
    ),
  },
  {
    signalId: "prototype-signal-pass",
    melbourneTip: "2:00 PM AEDT",
    tipUtc: inMinutes(232),
    ruleVersion: "B2B_COMBO_UNDER_v1.0",
    description: "Noah Benton Under 31.5 points + assists",
    gameLabel: "SAC @ LAL",
    bookmaker: "bet365",
    line: 31.5,
    price: 1.82,
    minimumOdds: 1.91,
    units: 0.5,
    stakeCents: 1_000,
    status: "BELOW_VALUE",
    evidence: [
      "Back-to-back combo under qualifier",
      "Exact-line conservative probability: 52.7%",
      "Best AU offer 1.82 is below minimum 1.91",
      "Pass until an executable AU book reaches 1.91",
    ],
    offers: offers(
      31.5,
      [1.8, 1.82, 1.78, 1.79, 1.81, 1.77, 1.8, 1.79, 1.78],
      1.9,
    ),
  },
  {
    signalId: "prototype-signal-bench",
    melbourneTip: "2:00 PM AEDT",
    tipUtc: inMinutes(12),
    ruleVersion: "BENCH_REBOUND_REGRESSION_v1.0",
    description: "Owen Park Under 7.5 rebounds",
    gameLabel: "SAC @ LAL",
    bookmaker: "betr",
    line: 7.5,
    price: 1.93,
    minimumOdds: 1.86,
    units: 0.5,
    stakeCents: 1_000,
    status: "AWAITING_LINEUP",
    evidence: [
      "Previous game rebound spike: 12",
      "Expected bench role",
      "Exact-line conservative probability: 54.4%",
      "Manual non-starter confirmation required",
    ],
    offers: offers(
      7.5,
      [1.89, 1.9, 1.88, 1.91, 1.9, 1.87, 1.89, 1.93, 1.86],
      1.84,
    ),
  },
];

export const PROTOTYPE_LINEUPS: readonly PrototypeLineupCandidate[] = [
  {
    candidateId: "prototype-lineup-1",
    playerName: "Owen Park",
    gameLabel: "SAC @ LAL",
    melbourneTip: "2:00 PM AEDT",
    tipUtc: inMinutes(46),
    recommendation: PROTOTYPE_RECOMMENDATIONS[4]!,
  },
];

export const PROTOTYPE_LEDGER: readonly PrototypeLedgerRow[] = [
  {
    betId: "prototype-bet-open",
    signalId: "prototype-history-open",
    placedAt: "2026-10-28T02:14:00.000Z",
    ruleVersion: "SHORT_REST_DOG_v1.0",
    description: "ORL +4.5 spread",
    gameLabel: "ATL @ ORL",
    bookmaker: "sportsbet",
    actualLine: 4.5,
    actualPrice: 1.91,
    stakeCents: 2_000,
    units: 1,
    status: "PLACED",
    result: null,
    profitUnits: null,
    closingLine: null,
    closingPrice: null,
    lineClv: null,
    priceClv: null,
    synthetic: true,
  },
  {
    betId: "prototype-bet-win",
    signalId: "prototype-history-win",
    placedAt: "2026-10-27T01:42:00.000Z",
    ruleVersion: "THREE_PM_REVERSION_v1.0",
    description: "Cal Redd Under 3.5 3PM",
    gameLabel: "MIL @ CLE",
    bookmaker: "tab",
    actualLine: 3.5,
    actualPrice: 1.92,
    stakeCents: 1_000,
    units: 0.5,
    status: "SETTLED",
    result: "WIN",
    profitUnits: 0.46,
    closingLine: 3.5,
    closingPrice: 1.82,
    lineClv: 0,
    priceClv: 0.1,
    synthetic: true,
  },
  {
    betId: "prototype-bet-loss",
    signalId: "prototype-history-loss",
    placedAt: "2026-10-26T03:08:00.000Z",
    ruleVersion: "ROAD_B2B_BOUNCE_v1.0",
    description: "Tomas Reed Over 19.5 points",
    gameLabel: "UTA @ POR",
    bookmaker: "bet365",
    actualLine: 19.5,
    actualPrice: 1.9,
    stakeCents: 1_000,
    units: 0.5,
    status: "SETTLED",
    result: "LOSS",
    profitUnits: -0.5,
    closingLine: 20.5,
    closingPrice: 1.91,
    lineClv: 1,
    priceClv: -0.01,
    synthetic: true,
  },
  {
    betId: "prototype-bet-push",
    signalId: "prototype-history-push",
    placedAt: "2026-10-25T04:22:00.000Z",
    ruleVersion: "SHORT_REST_DOG_v1.0",
    description: "DAL +6 spread",
    gameLabel: "DAL @ MIN",
    bookmaker: "ladbrokes",
    actualLine: 6,
    actualPrice: 1.91,
    stakeCents: 2_000,
    units: 1,
    status: "SETTLED",
    result: "PUSH",
    profitUnits: 0,
    closingLine: 5.5,
    closingPrice: 1.91,
    lineClv: -0.5,
    priceClv: 0,
    synthetic: true,
  },
  {
    betId: "prototype-bet-void",
    signalId: "prototype-history-void",
    placedAt: "2026-10-24T00:55:00.000Z",
    ruleVersion: "BENCH_REBOUND_REGRESSION_v1.0",
    description: "Milan Stone Under 6.5 rebounds",
    gameLabel: "CHA @ WAS",
    bookmaker: "betr",
    actualLine: 6.5,
    actualPrice: 1.88,
    stakeCents: 1_000,
    units: 0.5,
    status: "VOID",
    result: "VOID",
    profitUnits: 0,
    closingLine: null,
    closingPrice: null,
    lineClv: null,
    priceClv: null,
    synthetic: true,
  },
];

export const PROTOTYPE_HEALTH: PrototypeHealthView = {
  sport: "NBA",
  releaseState: "ACTIVE",
  live: true,
  ready: false,
  replayReady: true,
  usage: {
    highlightly: {
      used: 42,
      normalLimit: 90,
      hardLimit: 100,
      remaining: 58,
      optionalSamplingStopped: false,
      betCriticalExhausted: false,
      lastSuccessAt: "2026-10-20T23:50:00.000Z",
      lastFailureAt: null,
      projectedExhaustionAt: null,
    },
    rapidodds: {
      used: 60,
      normalLimit: 80,
      hardLimit: 100,
      remaining: 40,
      optionalSamplingStopped: false,
      betCriticalExhausted: false,
      lastSuccessAt: "2026-10-20T23:49:00.000Z",
      lastFailureAt: null,
      projectedExhaustionAt: null,
      budgetMode: "TRIAL",
      dailyUsed: 60,
      dailyLimit: 100,
      allowanceUsed: 1_200,
      allowanceLimit: 10_000,
    },
  },
  localAgent: {
    status: "ONLINE",
    displayName: "Windows PC",
    lastSeenAt: "2026-10-20T23:55:00.000Z",
    pendingJobs: 1,
  },
  components: [
    {
      sport: "NBA",
      component: "API",
      label: "API",
      releaseState: "ACTIVE",
      requiredForRelease: false,
      status: "HEALTHY",
      detail: "Synthetic requests responding normally",
    },
    {
      sport: "NBA",
      component: "Supabase",
      label: "Supabase",
      releaseState: "ACTIVE",
      requiredForRelease: false,
      status: "HEALTHY",
      detail: "Shown for workflow only; prototype makes no database writes",
    },
    {
      sport: "NBA",
      component: "Highlightly",
      label: "Highlightly",
      releaseState: "ACTIVE",
      requiredForRelease: true,
      status: "HEALTHY",
      detail: "Example schedule, results, and lineup calls are current",
    },
    {
      sport: "NBA",
      component: "Discord",
      label: "Discord",
      releaseState: "ACTIVE",
      requiredForRelease: false,
      status: "HEALTHY",
      detail: "Example alert and system webhook checks passed",
    },
    {
      sport: "NBA",
      component: "Historical replay",
      label: "Historical replay",
      releaseState: "ACTIVE",
      requiredForRelease: true,
      status: "HEALTHY",
      detail: "2025/26 validation replay passed with zero discrepancies",
    },
  ],
};
